import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    ConversationSetupData,
    ConversationSession,
    ConversationMessage,
    AvatarProfile,
    ConversationRecommendation
} from '../../features/student/StudentConversation/types';

interface ConversationState {
    // Setup state - persisted
    setupCompleted: boolean;
    setupData: ConversationSetupData | null;

    // Current session state
    currentSession: ConversationSession | null;
    selectedAvatar: AvatarProfile | null;
    isSessionActive: boolean;
    sessionPaused: boolean;
    sessionStartTime: Date | null;
    sessionDuration: number;

    // Messages and AI state
    messages: ConversationMessage[];
    isAiTyping: boolean;
    currentEmotion: 'happy' | 'confused' | 'neutral' | 'encouraging';

    // Recording state
    isRecording: boolean;

    // UI state
    recommendations: ConversationRecommendation[];
    isMuted: boolean;
    isFullscreen: boolean;
    showEndSessionDialog: boolean;

    // Loading and error states
    isLoading: boolean;
    error: string | null;
}

const initialState: ConversationState = {
    setupCompleted: false,
    setupData: null,
    currentSession: null,
    selectedAvatar: null,
    isSessionActive: false,
    sessionPaused: false,
    sessionStartTime: null,
    sessionDuration: 0,
    messages: [],
    isAiTyping: false,
    currentEmotion: 'neutral',
    isRecording: false,
    recommendations: [],
    isMuted: false,
    isFullscreen: false,
    showEndSessionDialog: false,
    isLoading: false,
    error: null,
};

const conversationSlice = createSlice({
    name: 'conversation',
    initialState,
    reducers: {
        // Setup actions
        setSetupData: (state, action: PayloadAction<ConversationSetupData>) => {
            state.setupData = action.payload;
            state.setupCompleted = true;
        },
        setSetupFromBackend: (state, action: PayloadAction<ConversationSetupData | null>) => {
            state.setupData = action.payload;
            state.setupCompleted = !!action.payload;
        },
        resetSetup: (state) => {
            state.setupData = null;
            state.setupCompleted = false;
        },

        // Session actions
        startSession: (state, action: PayloadAction<{ session: ConversationSession; avatar: AvatarProfile | null }>) => {
            state.currentSession = action.payload.session;
            state.selectedAvatar = action.payload.avatar;
            state.isSessionActive = true;
            state.sessionPaused = false;
            state.sessionStartTime = new Date();
            state.sessionDuration = 0;
            state.messages = [];
            state.showEndSessionDialog = false;
        },
        endSession: (state) => {
            state.isSessionActive = false;
            state.sessionPaused = false;
            state.currentSession = null;
            state.selectedAvatar = null;
            state.messages = [];
            state.sessionStartTime = null;
            state.sessionDuration = 0;
            state.isRecording = false;
            state.showEndSessionDialog = false;
        },
        pauseSession: (state) => {
            state.sessionPaused = !state.sessionPaused;
        },
        updateSessionDuration: (state, action: PayloadAction<number>) => {
            state.sessionDuration = action.payload;
        },

        // Message actions
        addMessage: (state, action: PayloadAction<ConversationMessage>) => {
            state.messages.push(action.payload);
        },
        setMessages: (state, action: PayloadAction<ConversationMessage[]>) => {
            state.messages = action.payload;
        },

        // AI state actions
        setAiTyping: (state, action: PayloadAction<boolean>) => {
            state.isAiTyping = action.payload;
        },
        setCurrentEmotion: (state, action: PayloadAction<'happy' | 'confused' | 'neutral' | 'encouraging'>) => {
            state.currentEmotion = action.payload;
        },

        // Recording actions
        setRecording: (state, action: PayloadAction<boolean>) => {
            state.isRecording = action.payload;
        },

        // UI actions
        setRecommendations: (state, action: PayloadAction<ConversationRecommendation[]>) => {
            state.recommendations = action.payload;
        },
        dismissRecommendation: (state, action: PayloadAction<number>) => {
            state.recommendations = state.recommendations.filter((_, index) => index !== action.payload);
        },
        toggleMute: (state) => {
            state.isMuted = !state.isMuted;
        },
        setMuted: (state, action: PayloadAction<boolean>) => {
            state.isMuted = action.payload;
        },
        toggleFullscreen: (state) => {
            state.isFullscreen = !state.isFullscreen;
        },
        setFullscreen: (state, action: PayloadAction<boolean>) => {
            state.isFullscreen = action.payload;
        },
        setShowEndSessionDialog: (state, action: PayloadAction<boolean>) => {
            state.showEndSessionDialog = action.payload;
        },

        // Loading and error actions
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
});

export const {
    // Setup actions
    setSetupData,
    setSetupFromBackend,
    resetSetup,

    // Session actions
    startSession,
    endSession,
    pauseSession,
    updateSessionDuration,

    // Message actions
    addMessage,
    setMessages,

    // AI state actions
    setAiTyping,
    setCurrentEmotion,

    // Recording actions
    setRecording,

    // UI actions
    setRecommendations,
    dismissRecommendation,
    toggleMute,
    setMuted,
    toggleFullscreen,
    setFullscreen,
    setShowEndSessionDialog,

    // Loading and error actions
    setLoading,
    setError,
    clearError,
} = conversationSlice.actions;

export default conversationSlice.reducer; 